import {
  variantBAssets,
  variantBProviders,
} from "../../Utils/variantBAssets";
import { SurveyJSForm } from "../../../modules/surveyjs_forms/SurveyJSForm";
import { lv2Theme } from "../../../modules/surveyjs_forms/surveyTheme";
import { LanderV2MarketingNav } from "../../../modules/common/menus/LanderV2MarketingNav";
import { LanderV2MarketingProps } from "../../Campaigns/Schemas/LanderV2/types";
import { TrustpilotReviewsCarousel } from "./TrustpilotReviewsCarousel";
import { ComplianceNote } from "./shared/ComplianceNote";

const processSteps = [
  {
    title: "1. Fill in the basics",
    copy: "Tell us a little about you and your pension in a quick, secure form.",
    image: variantBAssets.processFeedback,
  },
  {
    title: "2. We find the best deals",
    copy: "We find the highest-paying products that match your specific goals.",
    image: variantBAssets.processProfit,
  },
  {
    title: "3. Get FREE expert advice",
    copy: "Connect with qualified pensions advisor to ensure which product is right for you.",
    image: variantBAssets.processSchool,
  },
  {
    title: "4. Sit back and relax!",
    copy: "Congratulations. You have officially maximised your pension and guaranteed yourself the best deal.",
    image: variantBAssets.processRelax,
  },
] as const;

const heroChecks = [
  {
    key: "best-deals",
    content: (
      <>
        Find the <strong>best deals</strong> on the open market in minutes
      </>
    ),
  },
  {
    key: "free-advice",
    content: (
      <>
        Get <strong>FREE</strong> expert guidance on the right products
      </>
    ),
  },
  {
    key: "boost",
    content: (
      <>
        Boost your pension up to <strong>47%</strong>
      </>
    ),
  },
  {
    key: "free",
    content: (
      <>
        <strong>100% FREE</strong> - it doesn't cost you a penny!
      </>
    ),
  },
];

const tickGreenRibbedSrc = "/media/misc/tick_green_ribbed.png";
const fscsRibbedSrc = "/media/logos/fscs_ribbed.png";
const landerCallNumber = "0808 281 2558";
const landerCallHref = "tel:08082812558";

function scrollToEntryForm() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function QuoteEntrySurvey({
  id,
  entrySchemaPage,
  surveyProps,
  initialData,
  onQuoteSubmit,
  onFirstInteraction,
}: Pick<LanderV2MarketingProps, "entrySchemaPage" | "surveyProps" | "initialData" | "onQuoteSubmit" | "onFirstInteraction"> & { id: string }) {
  const handleComplete = (data?: Record<string, any>) => {
    return onQuoteSubmit(data ?? {});
  };

  return (
    <SurveyJSForm
      surveyFormId={id}
      pageSchemas={[entrySchemaPage]}
      initialData={initialData}
      clearDraftOnComplete={false}
      surveyProps={surveyProps}
      surveyTheme={lv2Theme}
      wrapperFormCssClass="lv2-survey lv2-entry-survey"
      onFirstInteraction={onFirstInteraction}
      onComplete={handleComplete}
    />
  );
}

export function LanderV2Marketing({
  entrySchemaPage,
  surveyProps,
  initialData,
  resolvedHeading,
  resolvedSubheading,
  onQuoteSubmit,
  onFirstInteraction,
  onBookAppointment,
}: LanderV2MarketingProps) {

  return (
    <article className="pa-vb">
      <LanderV2MarketingNav />

      <section className="pa-vb-hero" aria-labelledby="variant-b-title">
        <div className="pa-vb-hero-copy">
          <h1 id="variant-b-title">{resolvedHeading}</h1>
          <p className="m-0">{resolvedSubheading}</p>
          <div className="pa-vb-quote-entry">
            <QuoteEntrySurvey
              id="annuitycalculator-v3-quote-entry-hero"
              entrySchemaPage={entrySchemaPage}
              surveyProps={surveyProps}
              initialData={initialData}
              onQuoteSubmit={onQuoteSubmit}
              onFirstInteraction={onFirstInteraction}
            />
          </div>
          <ul className="pa-vb-checks">
            {heroChecks.map((check) => (
              <li key={check.key}>
                <span className="pa-vb-check-icon" aria-hidden="true">
                  <img src={tickGreenRibbedSrc} alt="" />
                  <i className="bi bi-check-lg"></i>
                </span>
                <span>{check.content}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="pa-vb-hero-art-wrap">
          <img
            className="pa-vb-hero-art"
            src={variantBAssets.heroIllustration}
            alt="Pension guidance illustration"
          />
        </div>
        <img className="pa-vb-hero-fscs" src={fscsRibbedSrc} alt="FSCS protected" />
      </section>

      <section
        className="pa-vb-section pa-vb-provider-section"
        aria-labelledby="variant-b-providers"
      >
        <h2 id="variant-b-providers" className="ux2-text-heading">
          We compare quotes from every major UK provider
        </h2>
        <div className="pa-vb-provider-strip">
          {variantBProviders.map(([name, src]) => (
            <img key={name} src={src} alt={name} />
          ))}
        </div>
      </section>

      <section className="pa-vb-process" aria-labelledby="variant-b-process">
        <h2 id="variant-b-process" className="ux2-text-heading">
          Getting the most from your pension has never been easier
        </h2>
        <div className="pa-vb-process-grid">
          {processSteps.map((step) => (
            <div className="pa-vb-process-step" key={step.title}>
              <img src={step.image} alt="" aria-hidden="true" />
              <div className="pa-vb-process-step-copy">
                <h3>{step.title}</h3>
                <p className="ux2-text-body">{step.copy}</p>
              </div>
            </div>
          ))}
        </div>
        <button
          className="pa-vb-button ux2-text-button"
          type="button"
          onClick={scrollToEntryForm}
        >
          GET STARTED
        </button>
      </section>

      <section
        className="pa-vb-section pa-vb-reviews"
        aria-labelledby="variant-b-reviews"
      >
        <h2 id="variant-b-reviews" className="ux2-text-heading">
          We have helped over 1,000 customers get the most from their pension
        </h2>
        <TrustpilotReviewsCarousel />
      </section>

      <section className="pa-vb-card-row" aria-label="Safety and quote start">
        <div className="pa-vb-safe-card">
          <img src={variantBAssets.fscsProtected} alt="FSCS protected" />
          <div>
            <h2 className="ux2-text-safe-heading">You are in safe hands!</h2>
            <p className="ux2-text-body-lg">
              We only partner with FCA-regulated advisors, meaning your pension
              switch is fully backed by the <strong>FSCS protection scheme.</strong>
            </p>
          </div>
        </div>
        <div className="pa-vb-quote-card">
          <h2 className="ux2-text-heading">Ready to see what your pension will payout?</h2>
          <p className="ux2-text-body">Get quotes in as little as 2 minutes</p>
          <div className="pa-vb-quote-entry pa-vb-quote-entry-card">
            <QuoteEntrySurvey
              id="annuitycalculator-v3-quote-entry-card"
              entrySchemaPage={entrySchemaPage}
              surveyProps={surveyProps}
              initialData={initialData}
              onQuoteSubmit={onQuoteSubmit}
              onFirstInteraction={onFirstInteraction}
            />
          </div>
        </div>
      </section>

      <section className="pa-vb-talk" aria-labelledby="variant-b-talk">
        <img
          src={variantBAssets.talkIllustration}
          alt="Appointment booking illustration"
        />
        <div>
          <h2 id="variant-b-talk" className="ux2-text-heading">Prefer to talk?</h2>
          <p className="ux2-text-body-lg">
            Speak directly to a specialist pension advisor for <strong>free</strong>, friendly
            guidance on maximising your retirement income with no strings
            attached.
          </p>
          <p className="pa-vb-phone ux2-text-phone">
            <span>Call free on</span>
            <a href={landerCallHref}><strong>{landerCallNumber}</strong></a>
            <span>or</span>
            <button type="button" className="ux2-text-button" onClick={onBookAppointment}>
              BOOK AN APPOINTMENT
              <i className="bi bi-calendar3" aria-hidden="true"></i>
            </button>
          </p>
        </div>
      </section>
      <ComplianceNote />
    </article>
  );
}
